import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import SaveIcon from "@mui/icons-material/Save";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Autocomplete,
  Backdrop,
  Button,
  CircularProgress,
  SpeedDial,
  SpeedDialAction,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { crmService } from "../../services/crm.service";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { duplicateCrmBackdrop } from "../../reducers/myDesigns/myDesignsSlice";

const PREFIX = "SaveCrmData";

const classes = {
  root: `${PREFIX}-root`,
  saveButton: `${PREFIX}-saveButton`,
  saveButtonDisabled: `${PREFIX}-saveButtonDisabled`,
  backdropStyle: `${PREFIX}-backdropStyle`,
  copyContainer: `${PREFIX}-copyContainer`,
  copyTitleStyle: `${PREFIX}-copyTitleStyle`,
  copyButtonStyle: `${PREFIX}-copyButtonStyle`,
  autocompleteStyle: `${PREFIX}-autocompleteStyle`,
  speedDialStyle: `${PREFIX}-speedDialStyle`,
  tooltipStyle: `${PREFIX}-tooltipStyle`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      width: "100%",
    },
    [`& .${classes.saveButton}`]: {
      position: "fixed",
      zIndex: "99",
      bottom: "16px",
      right: "16px",
    },
    [`& .${classes.saveButtonDisabled}`]: {
      position: "fixed",
      zIndex: "99",
      bottom: "16px",
      right: "16px",
      "& > button": {
        color: "rgba(48, 48, 48, 0.8)",
        backgroundColor: "rgba(211, 175, 95, 0.3)",
      },
    },
    [`& .${classes.backdropStyle}`]: {
      color: "#D3AF5F",
      zIndex: "1000",
    },
    [`& .${classes.copyContainer}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxWidth: "500px",
      margin: "0 1rem",
      height: "240px",
      padding: "1rem",
      borderRadius: "4px",
      backgroundColor: "#424242",
    },
    [`& .${classes.copyTitleStyle}`]: {
      marginBottom: "2rem",
    },
    [`& .${classes.copyButtonStyle}`]: {
      width: "50%",
    },
    [`& .${classes.autocompleteStyle}`]: {
      marginBottom: "2rem",
      width: "100%",
    },
    [`& .${classes.speedDialStyle}`]: {
      color: "#D3AF5F",
    },
    [`& .${classes.tooltipStyle}`]: {
      color: "rgba(255, 255, 255, 0.7)",
      fontSize: "1rem",
      fontWeight: "400",
    },
  };
});

export default function SaveCrmData({
  data,
  setData,
  selectedTarget,
  setSelectedTarget,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const currentDesign = useSelector((state) => state.myDesigns.currentDesign);
  const [isDisabled, setIsDisabled] = useState(false);
  const [copyBackdrop, setCopyBackdrop] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [selectedCopyTarget, setSelectedCopyTarget] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [existingData, setExistingData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const isCrmBackdropDuplicateOn = useSelector(
    (state) => state.myDesigns?.isCrmBackdropDuplicateOn
  );

  useEffect(() => {
    if (data._id) setIsSaved(true);
    else setIsSaved(false);
  }, [data]);

  // useEffect(() => {
  //   if (selectedTarget) {
  //     setFetching(true);
  //     crmService
  //       .getCrmDesign(selectedTarget.frameId)
  //       .then((res) => {
  //         setExistingData(res);
  //         setFetching(false);
  //       })
  //       .catch((err) => {
  //         if (err === 403)
  //           enqueueSnackbar(t("toasts.unauthorizedError"), {
  //             variant: "error",
  //           });
  //         else
  //           enqueueSnackbar(t("Crm.toasts.copySuccess"), { variant: "error" });
  //         setFetching(false);
  //       });
  //   }
  // }, [copyBackdrop]);

  useEffect(() => {
    if (data.myDesign.soldFrames.length === 0) setIsDisabled(false);
    data.myDesign.soldFrames.forEach((item) => {
      if (
        item.quantity.length === 0 ||
        item.size.length === 0 ||
        item.format.length === 0
      ) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
    });
  }, [data.myDesign.soldFrames]);

  function handleEmptyData(tmpData) {
    Object.entries(tmpData).forEach(([key, value]) => {
      if (value && typeof value === "object") handleEmptyData(value);
      if (
        (value && typeof value === "object" && !Object.keys(value).length) ||
        (typeof value === "string" && value.length === 0) ||
        value === null ||
        value === undefined
      ) {
        if (Array.isArray(tmpData)) tmpData.splice(key, 1);
        else {
          if (key === "buyerType") delete tmpData[key];
          else tmpData[key] = null;
        }
      }
    });
    return tmpData;
  }

  function handleCopyCrm() {
    let tmp = {
      ...data,
      vip: {
        ...data.vip,
        birthDate: dayjs(data.vip.birthDate).format("YYYY-MM-DD"),
      },
      myDesign: {
        ...data.myDesign,
        soldDate: dayjs(data.myDesign.soldDate).format("YYYY-MM-DD"),
      },
      buyer: {
        ...data.buyer,
        managers: data.buyer.managerDetails.map((item) => item.id),
      },
    };

    delete tmp._id;
    delete tmp.buyer.managerDetails;
    delete tmp.buyer.id;
    delete tmp.buyer._id;
    delete tmp.myDesign.id;
    delete tmp.myDesign._id;
    delete tmp.vip.id;
    delete tmp.vip._id;

    crmService
      .createCrm(
        selectedTarget.frameId,
        selectedCopyTarget.id,
        handleEmptyData(tmp)
      )
      .then(() => {
        setSelectedTarget(selectedCopyTarget);
        enqueueSnackbar(t("Crm.toasts.copySuccess"), { variant: "success" });
      })
      .catch((err) => {
        setCopyBackdrop(false);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("Crm.toasts.copyFail"), { variant: "error" });
      });
  }

  async function handleSave() {
    let tmp = {
      ...data,
      vip: {
        ...data.vip,
        nationality: data.vip.nationality.map((nat) => nat.cca2),
        birthDate: data.vip.birthDate
          ? dayjs(data.vip.birthDate).format("YYYY-MM-DD")
          : null,
      },
      myDesign: {
        ...data.myDesign,
        soldDate: data.myDesign.soldDate
          ? dayjs(data.myDesign.soldDate).format("YYYY-MM-DD")
          : null,
      },
      buyer: {
        ...data.buyer,
        managers: data.buyer.managerDetails.map((item) => item.id),
      },
    };

    delete tmp.buyer.managerDetails;
    setOpenBackdrop(true);
    // if data._id exist = update else create
    if (data._id) {
      crmService
        .updateCrm(handleEmptyData(tmp))
        .then(() => {
          enqueueSnackbar(t("Crm.toasts.updateSuccess"), {
            variant: "success",
          });
          setOpenBackdrop(false);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("Crm.toasts.updateFail"), { variant: "error" });
          setOpenBackdrop(false);
        });
    } else {
      crmService
        .createCrm(
          selectedTarget.frameId,
          selectedTarget.id,
          handleEmptyData(tmp)
        )
        .then((res) => {
          enqueueSnackbar(t("Crm.toasts.createSuccess"), {
            variant: "success",
          });
          setData((draft) => {
            draft._id = res._id;
          });
          setOpenBackdrop(false);
        })
        .catch((err) => {
          if (err === 403)
            enqueueSnackbar(t("toasts.unauthorizedError"), {
              variant: "error",
            });
          else
            enqueueSnackbar(t("Crm.toasts.createFail"), { variant: "error" });
          setOpenBackdrop(false);
        });
    }
  }
  return (
    <StyledDiv>
      <div className={classes.root}>
        <Backdrop className={classes.backdropStyle} open={openBackdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Backdrop
          className={classes.backdropStyle}
          open={isCrmBackdropDuplicateOn}
          onClick={() => dispatch(duplicateCrmBackdrop())}
        >
          {fetching ? (
            <CircularProgress color="inherit" />
          ) : currentDesign?.targets.length === existingData.length ? (
            <div
              className={classes.copyContainer}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Typography variant="h5" className={classes.copyTitleStyle}>
                {t("Crm.exportTitle")}
              </Typography>
              <Typography>{t("Crm.noTargetMessage")}</Typography>
            </div>
          ) : (
            <div
              className={classes.copyContainer}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Typography variant="h5" className={classes.copyTitleStyle}>
                {t("Crm.exportTitle")}
              </Typography>
              {currentDesign && (
                <Autocomplete
                  label="copy-targets"
                  id="to-copy-targets"
                  disableClearable
                  className={classes.autocompleteStyle}
                  // className={classes.copySelectStyle}
                  options={currentDesign.targets.filter(
                    (target) =>
                      target.id !== selectedTarget.id &&
                      !existingData.some((elem) => elem.targetId === target.id)
                  )}
                  getOptionLabel={(option) =>
                    option.label !== undefined && option.label !== ""
                      ? currentDesign.label +
                        " (" +
                        option.id +
                        ") " +
                        " - " +
                        option.label
                      : ""
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("myDesign.targetAutocomplete")}
                      variant="outlined"
                    />
                  )}
                  value={selectedCopyTarget}
                  onChange={(e, value) => setSelectedCopyTarget(value)}
                />
              )}
              <Button
                className={classes.copyButtonStyle}
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                size="large"
                id="copy-crm-button"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyCrm();
                }}
                disabled={selectedCopyTarget ? false : true}
              >
                {t("Crm.duplicate")}
              </Button>
            </div>
          )}
        </Backdrop>
        <SpeedDial
          ariaLabel="Save"
          className={
            !isDisabled ? classes.saveButton : classes.saveButtonDisabled
          }
          icon={<SaveIcon />}
          onClick={handleSave}
        />
      </div>
    </StyledDiv>
  );
}
